import React, { useMemo, useCallback } from 'react';
import { DataTable } from './common/DataTable';
import { adminApiClient } from '../lib/api';
import toast from 'react-hot-toast';

// Refactored Components
import { useTempleRegistrations } from './temple-registration/useTempleRegistrations';
import { getColumns } from './temple-registration/columns';
import { RegistrationDetailsModal } from './temple-registration/RegistrationDetailsModal';
import { RejectModal, SubscriptionModal } from './temple-registration/RegistrationActionModals';
import { RegistrationTabs } from './temple-registration/RegistrationTabs';
import { MediaManagementModal } from './temple-registration/MediaManagementModal';
import { ApplicationHistoryModal } from './temple-registration/ApplicationHistoryModal';

const TempleRegistrationPage: React.FC = () => {
    const {
        activeTab, setActiveTab,
        registrations, loading, error, setError,
        sorting, setSorting,
        pagination, setPagination,
        totalPages, totalRecords,
        filters, handleFilterChange,
        selectedRegistration, setShowDetailsModal, showDetailsModal,
        showRejectModal, setShowRejectModal,
        rejectionReason, setRejectionReason,
        subscriptionPlans, templeSubscription,
        selectedPlanId, setSelectedPlanId,
        showSubscriptionModal, setShowSubscriptionModal,
        templeId, searchingTemple,
        admins, selectedAdminId, setSelectedAdminId,
        applicationHistory,
        showHistoryModal, setShowHistoryModal,
        fetchingHistory,
        historyTempleName,

        // Actions
        fetchRegistrations,
        handleViewDetails,
        handleViewHistory,
        handleDelete,
        handleAssignSubscription,
        handleRemoveSubscription,
        handleReviewAction,
        handleAssignToMe,
        handleAssignTo,
        handleUpdateVerification,
        handleReject,
        handleUpdateTemple,
        findAndSetTempleId,
        onOpenSubscriptionModal,
        templeDetails,
        fetchTempleDetails,
        handleSaveRegistration,
        currentUser
    } = useTempleRegistrations();

    const [showMediaModal, setShowMediaModal] = React.useState(false);
    const [selectedMediaTempleId, setSelectedMediaTempleId] = React.useState<string | null>(null);

    const handleManageMedia = useCallback(async (reg: any) => {
        const tId = typeof reg.createdTemple === 'string' ? reg.createdTemple : reg.createdTemple?._id;
        if (tId) {
            setSelectedMediaTempleId(tId);
            await fetchTempleDetails(tId);
            setShowMediaModal(true);
        }
    }, [fetchTempleDetails]);

    const handleTogglePopular = useCallback(async (templeId: string, isPopular: boolean) => {
        try {
            const response = await adminApiClient.updateTemple(templeId, { isPopular });
            if (response.success) {
                toast.success(`Temple marked as ${isPopular ? 'Popular' : 'Standard'}`);
                fetchRegistrations(); // Refresh list to show updated status
            } else {
                toast.error(response.message || 'Failed to update status');
            }
        } catch (error) {
            console.error('Error toggling popular status:', error);
            toast.error('Failed to update popular status');
        }
    }, [fetchRegistrations]);

    const columns = useMemo(() => getColumns({
        activeTab,
        pagination,
        handleViewDetails,
        handleDelete,
        onManageMedia: handleManageMedia,
        onTogglePopular: handleTogglePopular,
        onViewHistory: handleViewHistory
    }), [activeTab, pagination, handleViewDetails, handleViewHistory, handleDelete, handleManageMedia, handleTogglePopular]);

    if (loading && registrations.length === 0) {
        return (
            <div className="page-loading">
                <div className="loading-spinner"></div>
                <p>Loading temple registrations...</p>
            </div>
        );
    }

    return (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 bg-gray-50 min-h-screen font-sans">
            {/* Header */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 mb-8">
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                    <div>
                        <h2 className="text-3xl font-bold text-gray-900 tracking-tight">Temple Registration Management</h2>
                        <p className="mt-2 text-sm text-gray-500">Review and manage temple registration requests and approvals.</p>
                    </div>
                    <button
                        onClick={fetchRegistrations}
                        className="inline-flex items-center justify-center px-4 py-2 border border-gray-300 rounded-lg shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500 transition-all duration-200"
                        disabled={loading}
                    >
                        {loading ? <span className="animate-spin mr-2">⏳</span> : <span className="mr-2 text-gray-500">🔄</span>}
                        Refresh Data
                    </button>
                </div>
            </div>

            {error && (
                <div className="error-banner">
                    <span className="error-icon">⚠️</span>
                    <span>{error}</span>
                    <button onClick={() => setError(null)} className="error-close">×</button>
                </div>
            )}

            <RegistrationTabs activeTab={activeTab} onTabChange={setActiveTab} />

            {/* Filters */}
            <div className="flex flex-col sm:flex-row gap-4 mb-6">
                <div className="relative flex-1">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <span className="text-gray-500 sm:text-sm">🔍</span>
                    </div>
                    <input
                        type="text"
                        placeholder="Search registrations..."
                        value={filters.search}
                        onChange={(e) => handleFilterChange('search', e.target.value)}
                        className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-orange-500 focus:border-orange-500 sm:text-sm transition duration-150 ease-in-out"
                    />
                </div>
            </div>

            {/* Table */}
            <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
                <DataTable
                    data={registrations}
                    columns={columns}
                    sorting={sorting}
                    onSortingChange={setSorting}
                    pagination={pagination}
                    onPaginationChange={setPagination}
                    pageCount={totalPages}
                    totalRecords={totalRecords}
                    loading={loading}
                />
            </div>

            {/* Modals */}
            <RegistrationDetailsModal
                isOpen={showDetailsModal}
                onClose={() => setShowDetailsModal(false)}
                selectedRegistration={selectedRegistration}
                loading={loading}
                activeTab={activeTab}
                onAssignToMe={handleAssignToMe}
                onUpdateVerification={handleUpdateVerification}
                onReviewClick={handleReviewAction}
                admins={admins}
                selectedAdminId={selectedAdminId}
                setSelectedAdminId={setSelectedAdminId}
                onAssignTo={handleAssignTo}
                templeId={templeId}
                templeSubscription={templeSubscription}
                templeDetails={templeDetails}
                searchingTemple={searchingTemple}
                onSearchTemple={findAndSetTempleId}
                onOpenSubscriptionModal={onOpenSubscriptionModal}
                onRemoveSubscription={handleRemoveSubscription}
                onUpdateTemple={handleUpdateTemple}
                onSave={handleSaveRegistration}
                currentUserId={currentUser?._id || currentUser?.id}
            />

            <MediaManagementModal
                isOpen={showMediaModal}
                onClose={() => setShowMediaModal(false)}
                templeId={selectedMediaTempleId || ''}
                initialData={{
                    logo: templeDetails?.logo,
                    banner: templeDetails?.banner,
                    images: templeDetails?.images
                }}
                onSuccess={() => {
                    if (selectedMediaTempleId) fetchTempleDetails(selectedMediaTempleId);
                }}
            />

            <ApplicationHistoryModal
                isOpen={showHistoryModal}
                onClose={() => setShowHistoryModal(false)}
                history={applicationHistory}
                loading={fetchingHistory}
                templeName={historyTempleName}
            />

            <RejectModal
                isOpen={showRejectModal}
                onClose={() => setShowRejectModal(false)}
                onReject={handleReject}
                templeName={selectedRegistration?.temple?.name || ''}
                reason={rejectionReason}
                setReason={setRejectionReason}
                loading={loading}
            />

            <SubscriptionModal
                isOpen={showSubscriptionModal}
                onClose={() => setShowSubscriptionModal(false)}
                onAssign={handleAssignSubscription}
                plans={subscriptionPlans}
                selectedPlanId={selectedPlanId}
                setSelectedPlanId={setSelectedPlanId}
                loading={loading}
                error={error}
            />
        </div>
    );
};

export default TempleRegistrationPage;